/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Component } from 'react';
import autobind from 'class-autobind';
import { View, StyleSheet } from 'react-native';

import Imm from '../../support/ImmutableHelpers';
import PricingActions, {
  getEmptyMarketPrice,
  getEmptyBulkPrice,
} from '../../support/PricingActions';
import IndexedList from '../../support/shared/IndexedList';

import { Button, CurrencyInput, IntegerInput, Text, XButton } from '../uiold';

import { Market } from '../../types/shared/Market';
import { Tier, BulkPrice } from '../../types/shared/Tier';

type Props = {
  accountCurrency: string;
  market: Market;
  priceTier: Tier;
  disabled?: boolean;
};

type State = {
  bulkPrices: IndexedList<BulkPrice>;
};

const emptyMarketPrice = getEmptyMarketPrice();

class PricingMarketItem extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    autobind(this);
    this.state = {
      bulkPrices: this._getBulkPrices(props),
    };
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props && this.props !== prevProps) {
      this.setState({
        bulkPrices: this._getBulkPrices(this.props),
      });
    }
  }

  render() {
    let { market, accountCurrency, disabled } = this.props;
    let pricing = this._getMarketPricing(this.props);
    return (
      <View style={styles.formField}>
        <Text style={styles.headingFour}>{market.name}</Text>
        <Text>{t('Price per Market Date')}</Text>
        <View style={styles.inline}>
          <View style={styles.mediumInput}>
            <CurrencyInput
              disabled={disabled}
              accountCurrency={accountCurrency}
              onChange={this._marketPriceChange}
              defaultValue={pricing.per_date}
            />
          </View>
          {!disabled && (
            <Button
              onPress={this._addBulkPrice}
              title=""
              iconName="plusCircle"
            />
          )}
        </View>
        <View>{this._renderBulkPrices()}</View>
      </View>
    );
  }

  _renderBulkPrices(): React.ReactNode {
    let { bulkPrices } = this.state;
    let { accountCurrency } = this.props;
    return bulkPrices.mapToArray((item) => {
      let onCountChange = (value: number) =>
        this._updateBulkPrice(item, 'min_date_count', value);
      let onPriceChange = (value: number) =>
        this._updateBulkPrice(item, 'per_date', value);
      let onRemove = () => this._removeBulkPrice(item);
      // Note: onChange fires only after blur.
      return (
        <View style={[styles.formField, styles.inlineBlock]} key={item.id}>
          {t.jsx(
            'When <numberOfDates></numberOfDates> or more date(s) selected, charge <price></price> per market date.',
            {
              numberOfDates: () => (
                <View style={styles.smallInput}>
                  <IntegerInput
                    onChange={onCountChange}
                    defaultValue={item.min_date_count}
                  />
                </View>
              ),
              price: () => (
                <View style={styles.smallInput}>
                  <CurrencyInput
                    accountCurrency={accountCurrency}
                    onChange={onPriceChange}
                    defaultValue={item.per_date}
                  />
                </View>
              ),
              _: (content) => <Text>{content}</Text>,
            },
          )}
          <XButton onPress={onRemove} style={styles.remove} />
        </View>
      );
    });
  }

  _updateBulkPrice(item: BulkPrice, name: string, value: number) {
    let { bulkPrices } = this.state;
    let newItem = Imm.setProperty(item, name as any, value);
    let index = bulkPrices.getIndexByID(item.id);
    let newList = bulkPrices.setByIndex(index, newItem);
    // sort the list by min_date_count
    // TODO: Sort only after we've totally finished. Use setTimeout after blur.
    if (name === 'min_date_count' && newList !== bulkPrices) {
      newList = newList.sortByKey(
        'min_date_count',
        (a, b) => Number(a) - Number(b),
      );
    }
    if (newList !== bulkPrices) {
      this._bulkPriceChange(newList);
    }
  }

  _removeBulkPrice(item: BulkPrice) {
    let { bulkPrices } = this.state;
    let index = bulkPrices.getIndexByID(item.id);
    let newList = bulkPrices.splice(index, 1);
    if (newList !== bulkPrices) {
      this._bulkPriceChange(newList);
    }
  }

  _addBulkPrice() {
    let { bulkPrices } = this.state;
    let newList = bulkPrices.unshift(getEmptyBulkPrice());
    if (newList !== bulkPrices) {
      this._bulkPriceChange(newList);
    }
  }

  _getMarketPricing(props: Props) {
    let { priceTier, market } = props;
    return priceTier.markets[market.id] || emptyMarketPrice;
  }

  _getBulkPrices(props: Props): IndexedList<BulkPrice> {
    let marketPrice = this._getMarketPricing(props);
    return new IndexedList(marketPrice.bulk);
  }

  _bulkPriceChange(bulkPrices: IndexedList<BulkPrice>) {
    let { priceTier, market } = this.props;
    PricingActions.updateMarketPricing(priceTier, market.id, {
      bulk: bulkPrices.toArray(),
    });
  }

  _marketPriceChange(value: number) {
    let { priceTier, market } = this.props;
    let pricing = this._getMarketPricing(this.props);
    if (value !== pricing.per_date) {
      PricingActions.updateMarketPricing(priceTier, market.id, {
        per_date: value,
      });
    }
  }
}

let styles = StyleSheet.create({
  headingFour: {
    fontSize: 14,
    lineHeight: 22,
    marginVertical: 4,
    fontWeight: '600',
  },
  inline: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  inlineBlock: {
    paddingRight: 5,
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  smallInput: {
    flex: 1,
  },
  mediumInput: {
    width: 230,
  },
  formField: {
    marginVertical: 10,
  },
  remove: {
    marginLeft: 5,
  },
});

export default PricingMarketItem;
